import { useEffect, useState } from 'react';

import { Alert, Empty, Select, Space, Spin, Tag, Tree, Typography } from 'antd';

import { getCodeSerieTree } from '../../../api/Get';
import { searchSerie } from '../../../api/Search';

const { Text } = Typography;

/*
 * Choix précis de ce qu'un code doit ouvrir : une série entière, une saison, ou un épisode.
 *
 * L'arbre part de l'état actuel du code — les épisodes déjà ouverts sont cochés. Cocher en
 * ajoute, décocher en retire : le parent remonte la différence, ce qui évite d'avoir à
 * raisonner en « ajouts » et « retraits » séparés.
 */
const SeriePicker = ({ code, onChange }) => {
  const [options, setOptions] = useState([]);
  const [idSerie, setIdSerie] = useState(null);
  const [serie, setSerie] = useState(null);
  const [checked, setChecked] = useState([]);
  const [initial, setInitial] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const search = async value => {
    if (!value) return;
    const res = await searchSerie(value);
    if (res && res.series) setOptions(res.series.map(s => ({ label: s.name, value: s.id })));
  };

  useEffect(() => {
    if (!idSerie) {
      setSerie(null);
      return;
    }

    let annule = false;
    setLoading(true);
    setError('');

    getCodeSerieTree(code, idSerie).then(res => {
      if (annule) return;
      setLoading(false);
      if (res.error) {
        setError(res.error);
        return;
      }
      const dejaOuverts = res.serie.saisons.flatMap(sa => sa.episodes.filter(e => e.dansLeCode).map(e => `ep-${e.id}`));
      setSerie(res.serie);
      setInitial(dejaOuverts);
      setChecked(dejaOuverts);
    });

    return () => {
      annule = true;
    };
  }, [code, idSerie]);

  // Les ajouts et les retraits se déduisent de l'écart avec l'état de départ.
  useEffect(() => {
    if (!serie) return;
    const idOf = key => Number(String(key).replace('ep-', ''));
    const coches = checked.filter(k => String(k).startsWith('ep-'));

    onChange({
      addEpisodes: coches.filter(k => !initial.includes(k)).map(idOf),
      removeEpisodes: initial.filter(k => !coches.includes(k)).map(idOf),
    });
  }, [checked, initial, onChange, serie]);

  const treeData = serie
    ? [
        {
          children: serie.saisons.map(sa => ({
            children: sa.episodes.map(ep => ({
              key: `ep-${ep.id}`,
              title: (
                <Space size={4}>
                  <Text>{`${ep.numero}. ${ep.nom}`}</Text>
                  {ep.dansLeCode && <Tag color="blue">déjà ouvert</Tag>}
                </Space>
              ),
            })),
            key: `sa-${sa.id}`,
            title: <Text strong>{`Saison ${sa.numero}`}</Text>,
          })),
          key: `se-${serie.id}`,
          title: <Text strong>{serie.name}</Text>,
        },
      ]
    : [];

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <Select
        allowClear
        showSearch
        filterOption={false}
        onChange={value => setIdSerie(value ?? null)}
        onSearch={search}
        options={options}
        placeholder="Rechercher une série"
        style={{ maxWidth: 420, width: '100%' }}
        value={idSerie}
      />

      {error && <Alert message={error} showIcon type="error" />}

      {loading && <Spin />}

      {!loading && !serie && <Empty description="Choisir une série pour voir ses saisons et ses épisodes" />}

      {!loading && serie && (
        <>
          <Text type="secondary">
            Cocher une série, une saison ou un épisode l&apos;ajoute au code ; décocher ce qui est déjà ouvert le retire.
          </Text>
          <Tree
            checkable
            defaultExpandAll
            checkedKeys={checked}
            onCheck={keys => setChecked(Array.isArray(keys) ? keys : keys.checked)}
            selectable={false}
            treeData={treeData}
          />
        </>
      )}
    </Space>
  );
};

export default SeriePicker;
